import { FunctionComponent } from 'react'
import {
  RFPDocumentSummary,
  RFPSortBy,
  SiteRFPDocumentSummary,
  SiteRFPPhaseAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Pagination,
  Skeleton,
  Typography,
} from '@black-bear-energy/rucksack'
import houseSearch from '../../assets/house-search.svg'
import { MenuAlign } from '../common/MenuButton'
import { CustomerRFPTable } from './CustomerRFPTable'
import { SearchAndFilterBar } from './SearchAndFilterBar'
import { SiteRFPTable } from './SiteRFPTable'
import type { SortButtonProps } from '../common/SortButton'
import { Surface } from '../common/Surface'

export const SearchableTable: FunctionComponent<SearchableTableProps> = ({
  variant,
  records,
  isPending,
  search,
  setSearch,
  requiredMissing,
  setRequiredMissing,
  reviewMissing,
  setReviewMissing,
  phase,
  setPhase,
  page,
  setPage,
  totalPages,
  sort,
  filterAlign,
}) => {
  const defaultTableHeight = '45.25rem'
  const searchBarLabel =
    variant === 'customerRFP'
      ? 'Search by project, site, address, channel partner, or pod member'
      : 'Search by site or address'
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value)
  }

  return (
    <Surface>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SearchAndFilterBar
          searchBarLabel={searchBarLabel}
          search={search}
          setSearch={setSearch}
          requiredMissing={requiredMissing}
          setRequiredMissing={setRequiredMissing}
          reviewMissing={reviewMissing}
          setReviewMissing={setReviewMissing}
          phase={phase}
          setPhase={setPhase}
          sort={sort}
          filterAlign={filterAlign}
        />
        {isPending && (
          <Skeleton variant="rounded" height={defaultTableHeight} />
        )}
        {!isPending && (
          <>
            {!(records && records.length > 0) ? (
              <Box
                sx={{
                  width: '100%',
                  height: defaultTableHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2.5,
                  py: 5,
                }}
              >
                <img src={houseSearch} alt="" />
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  No results found.
                  <br />
                  Please try a new search.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ minHeight: defaultTableHeight }}>
                {variant === 'customerRFP' ? (
                  <CustomerRFPTable
                    rfpDocumentSummaries={records as RFPDocumentSummary[]}
                    searchString={search}
                  />
                ) : (
                  <SiteRFPTable
                    siteRFPDocumentSummaries={
                      records as SiteRFPDocumentSummary[]
                    }
                    searchString={search}
                  />
                )}
              </Box>
            )}
          </>
        )}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={totalPages}
            size="large"
            page={page}
            onChange={handlePageChange}
          />
        </Box>
      </Box>
    </Surface>
  )
}

export interface SearchableTableProps {
  variant: 'customerRFP' | 'siteRFP'
  records?: RFPDocumentSummary[] | SiteRFPDocumentSummary[]
  isPending: boolean
  search: string
  setSearch: (search: string) => void
  requiredMissing: boolean
  setRequiredMissing: (requiredMissing: boolean) => void
  reviewMissing: boolean
  setReviewMissing: (reviewMissing: boolean) => void
  phase: SiteRFPPhaseAPIResponse[]
  setPhase: (phase: SiteRFPPhaseAPIResponse[]) => void
  page: number
  setPage: (page: number) => void
  totalPages: number
  sort?: SortButtonProps<RFPSortBy>
  filterAlign?: MenuAlign
}
